import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext({
  user: null,
  loading: true,
  override: null,
  shift: null,
  needsShiftOpen: false,
  login: async () => {},
  logout: async () => {},
  refresh: async () => {},
  requestOverride: async () => {},
  clearOverride: () => {},
  setShift: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Login-time shift snapshot returned by /auth/login. Drives the
  // one-shot cash-start prompt shown by PostLoginShiftPrompt.
  const [shift, setShift] = useState(null);
  const [needsShiftOpen, setNeedsShiftOpen] = useState(false);
  // Manager override — { manager: {id,name,role}, expires_at: ISO, remaining_sec: int }
  const [override, setOverride] = useState(() => {
    try {
      const tok = localStorage.getItem("override_token");
      const exp = localStorage.getItem("override_expires_at");
      const mgr = localStorage.getItem("override_manager");
      if (tok && exp && new Date(exp).getTime() > Date.now()) {
        return {
          manager: JSON.parse(mgr),
          expires_at: exp,
          remaining_sec: Math.max(0, Math.floor((new Date(exp).getTime() - Date.now()) / 1000)),
        };
      }
    } catch {
      /* noop */
    }
    return null;
  });

  const refresh = useCallback(async () => {
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }
      const r = await http.get("/auth/me");
      setUser(r.data);
      localStorage.setItem("auth_user", JSON.stringify(r.data));
      // Rehydrate open-shift status so the post-login prompt can fire
      // on page reload if the operator never opened one.
      try {
        const sh = await http.get("/shifts/current");
        setShift(sh.data?.shift || null);
        setNeedsShiftOpen(!sh.data?.shift);
      } catch {
        setShift(null);
      }
    } catch {
      setUser(null);
      localStorage.removeItem("auth_token");
      localStorage.removeItem("auth_user");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(refresh, 0);
    return () => clearTimeout(t);
  }, [refresh]);

  const login = useCallback(async (pin, tenantCode, opts = {}) => {
    const body = { pin };
    if (tenantCode) body.tenant_code = tenantCode;
    if (opts.force) body.force = true;
    const r = await http.post("/auth/login", body);
    localStorage.setItem("auth_token", r.data.token);
    localStorage.setItem("auth_user", JSON.stringify(r.data.user));
    if (tenantCode) localStorage.setItem("tenant_code", tenantCode);
    setUser(r.data.user);
    setShift(r.data.shift || null);
    setNeedsShiftOpen(!!r.data.needs_shift_open);
    return r.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await http.post("/auth/logout");
    } catch {
      /* ignore */
    }
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_user");
    localStorage.removeItem("override_token");
    localStorage.removeItem("override_expires_at");
    localStorage.removeItem("override_manager");
    setUser(null);
    setOverride(null);
    setShift(null);
    setNeedsShiftOpen(false);
  }, []);

  // Best-effort logout on tab close so the operator can log back in
  // from another device without being locked out by the single-active
  // session gate. Uses ``navigator.sendBeacon`` (fire-and-forget POST
  // that survives page unload) with the same bearer token as the app.
  useEffect(() => {
    if (!user) return undefined;
    const onUnload = () => {
      try {
        const token = localStorage.getItem("auth_token");
        if (!token) return;
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
        // ``fetch`` with keepalive survives the page-unload path AND
        // lets us send the Authorization header (which sendBeacon
        // cannot). The response is discarded — this is fire-and-forget.
        fetch(`${base}/api/auth/logout`, {
          method: "POST",
          keepalive: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: "{}",
        }).catch(() => { /* ignore */ });
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("pagehide", onUnload);
    return () => window.removeEventListener("pagehide", onUnload);
  }, [user]);

  // ----- Manager override -----
  const requestOverride = useCallback(async (pin, opts = {}) => {
    const r = await http.post("/auth/manager-override", {
      pin,
      duration_seconds: opts.duration_seconds || 300,
      reason: opts.reason || null,
    });
    const d = r.data;
    localStorage.setItem("override_token", d.override_token);
    localStorage.setItem("override_expires_at", d.expires_at);
    localStorage.setItem("override_manager", JSON.stringify(d.manager));
    const remaining_sec = Math.max(0, Math.floor((new Date(d.expires_at).getTime() - Date.now()) / 1000));
    setOverride({ manager: d.manager, expires_at: d.expires_at, remaining_sec });
    return d;
  }, []);

  const clearOverride = useCallback(() => {
    localStorage.removeItem("override_token");
    localStorage.removeItem("override_expires_at");
    localStorage.removeItem("override_manager");
    setOverride(null);
  }, []);

  // ----- Override prompt (global trigger so ANY disabled button can
  // open the dialog via a toast action). Components call
  // ``requestOverridePrompt(reason, onGranted)`` and Layout renders the
  // dialog once. The pending action runs only on successful grant.
  const [overridePrompt, setOverridePrompt] = useState(null); // { reason, onGranted } | null
  const requestOverridePrompt = useCallback((reason, onGranted) => {
    setOverridePrompt({ reason, onGranted });
  }, []);
  const dismissOverridePrompt = useCallback(() => setOverridePrompt(null), []);

  // ``guard(perm, reason, action)`` is the convenience wrapper used by
  // every interactive button that needs a manager override fallback.
  // If the caller already has the perm (or an active override) → just
  // runs ``action``. Otherwise it pops a toast with «Ζητήστε έγκριση
  // Manager» button that opens the override dialog; on grant, the
  // pending ``action`` fires automatically.
  const guard = useCallback((perm, reason, action) => {
    const hasPerm = (() => {
      if (!user) return false;
      if (user.role === "super_manager") return true;
      if (override && override.remaining_sec > 0) return true;
      return !!(user.permissions && user.permissions[perm]);
    })();
    if (hasPerm) {
      action?.();
      return;
    }
    toast.error(`Ανεπαρκή δικαιώματα: ${reason}`, {
      duration: 6000,
      action: {
        label: "Έγκριση Manager",
        onClick: () => requestOverridePrompt(reason, action),
      },
    });
  }, [user, override, requestOverridePrompt]);

  // Countdown — ticks once per second. Auto-clears at expiry.
  useEffect(() => {
    if (!override) return;
    const tick = () => {
      const remaining = Math.max(0, Math.floor((new Date(override.expires_at).getTime() - Date.now()) / 1000));
      if (remaining <= 0) {
        clearOverride();
        return;
      }
      setOverride((o) => (o ? { ...o, remaining_sec: remaining } : null));
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [override?.expires_at, clearOverride]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider value={{
      user, loading, override, login, logout, refresh,
      requestOverride, clearOverride,
      shift, needsShiftOpen, setShift, setNeedsShiftOpen,
      // Inline override prompt:
      overridePrompt, requestOverridePrompt, dismissOverridePrompt, guard,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

/**
 * Convenience permission helper.
 *
 * Behaviour:
 *   • super_manager + missing user = ALWAYS granted (backstop).
 *   • Active manager override → ALL perms granted for the duration.
 *   • Otherwise reads `user.permissions[key]` as a boolean.
 */
export function usePermissions() {
  const { user, override, guard } = useContext(AuthContext);
  const can = (key) => {
    if (!user) return false;
    if (user.role === "super_manager") return true;
    if (override && override.remaining_sec > 0) return true;
    return !!(user.permissions && user.permissions[key]);
  };
  return { user, can, guard, role: user?.role, override };
}
